import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { useObservedQuery } from './use-observed-query';
import { useChatsRow } from './use-conversation-row';
import { parseWaGroupRoster, type WaGroupRoster } from './wa-group-roster';

/** The roster read's own key — NOT under `chats-v1`, whose cache helpers assume an infinite list's `pages`. */
export const waGroupRosterKey = (conversationId: string | null | undefined) => ['wa-group-roster', conversationId ?? null] as const;

/**
 * One WhatsApp-group thread's roster, from the chats-v1 list (`ChatSummary.wa_group`), the only read that carries it.
 * The WhatsApp page of that list (up to 100 rows) is read and the thread's row picked out.
 */
async function fetchWaGroupRoster(conversationId: string): Promise<WaGroupRoster | null> {
  const params = new URLSearchParams({ contract: 'chats-v1', limit: '100', channel: 'WHATSAPP' });
  const res = await apiFetch(`${API_BASE}/api/conversations?${params}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`wa_group_roster_${res.status}`);
  const page = (await res.json().catch(() => null)) as { conversations?: Array<{ id?: string; waGroup?: unknown }> } | null;
  const row = page?.conversations?.find((c) => c.id === conversationId);
  return parseWaGroupRoster(row?.waGroup);
}

/**
 * The roster of a WhatsApp-group thread (the chat header's subtitle, the setup card's member states). The Chats list's
 * own row wins when it holds one — it re-reads on every list refresh — else this reads it once, and again on focus
 * after the stale time (the observer's refetch-on-focus). No polling loop.
 */
export function useWaGroupRoster(conversationId: string | null | undefined, enabled: boolean): WaGroupRoster | null {
  const chatsRow = useChatsRow(enabled && conversationId ? conversationId : undefined);
  const fromList = parseWaGroupRoster(chatsRow?.waGroup);
  const query = useObservedQuery({
    queryKey: waGroupRosterKey(conversationId),
    queryFn: () => fetchWaGroupRoster(conversationId as string),
    enabled: enabled && !!conversationId && !fromList,
    staleTime: 15_000,
    retry: false,
  });
  return fromList ?? query.data ?? null;
}
